/**
 * ReuploadDialog — request-reupload action (EPIC-006 Phase 5, Task 5.3).
 *
 * Requires reason 5–500 chars. Submit button is disabled until valid.
 * Calls POST /admin/seller-applications/{id}/request-reupload.
 */
import { useCallback, useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { BottomSheet, Button } from "@garden/ui";
import { ApiError } from "@/lib/api-client";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useRequestReupload } from "@/features/admin/hooks/useSellerApplications";

export interface ReuploadDialogProps {
  open: boolean;
  applicationId: string;
  onClose: () => void;
  onSuccess: () => void;
  onInvalidTransition: () => void;
}

const MIN_REASON = 5;
const MAX_REASON = 500;

export function ReuploadDialog({
  open,
  applicationId,
  onClose,
  onSuccess,
  onInvalidTransition,
}: ReuploadDialogProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const { mutateAsync, isPending } = useRequestReupload();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Reset on open/close
  useEffect(() => {
    if (!open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  const isValid = reason.length >= MIN_REASON && reason.length <= MAX_REASON;

  const handleReupload = useCallback(async () => {
    if (!isValid) return;
    setError(null);
    try {
      await mutateAsync({ id: applicationId, reason });
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.problem?.code === "seller_application.invalid_transition") {
          onInvalidTransition();
          return;
        }
      }
      setError(t("error.generic"));
    }
  }, [applicationId, reason, isValid, mutateAsync, onSuccess, onInvalidTransition, t]);

  return (
    <BottomSheet open={open} onClose={onClose} title={t("reupload.title")}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="reupload-reason"
            className="text-sm font-medium"
          >
            {t("reupload.reasonLabel")}
          </label>
          <textarea
            id="reupload-reason"
            data-testid="reupload-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("reupload.reasonPlaceholder")}
            rows={4}
            maxLength={MAX_REASON}
            className="w-full resize-none rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
            aria-describedby="reupload-reason-hint"
          />
          <p
            id="reupload-reason-hint"
            className="text-xs text-[var(--g-secondary)]"
          >
            {t("reupload.reasonHint")} ({reason.length}/{MAX_REASON})
          </p>
        </div>

        {error ? (
          <p className="text-sm text-[var(--g-error)]">{error}</p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={isPending}
            className="min-h-[44px]"
          >
            {t("reupload.cancel")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            data-testid="reupload-confirm-btn"
            onClick={() => void handleReupload()}
            disabled={!isValid || isPending}
            className="min-h-[44px] border-[var(--g-warn)] text-[var(--g-warn)] hover:bg-[var(--g-warn)]/10 disabled:opacity-50"
          >
            {t("reupload.confirm")}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}

export default ReuploadDialog;
